import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface CalendarProps {
  events?: {
    id: number;
    title: string;
    date: string;
    committee: string;
    type: string;
  }[];
  onSelectDate?: (date: Date) => void;
  onSelectEvent?: (eventId: number) => void;
}
export function Calendar({
  events = [],
  onSelectDate,
  onSelectEvent
}: CalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  // Get days in current month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  // Month names
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  // Day names
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Previous month
  const prevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  // Next month
  const nextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };
  // Handle date click
  const handleDateClick = (day: number) => {
    const selectedDate = new Date(currentYear, currentMonth, day);
    setSelectedDate(selectedDate);
    if (onSelectDate) {
      onSelectDate(selectedDate);
    }
  };
  // Handle event click
  const handleEventClick = (eventId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelectEvent) {
      onSelectEvent(eventId);
    }
  };
  // Check if a date has events
  const getEventsForDate = (day: number) => {
    const dateString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => event.date === dateString);
  };
  // Generate calendar days
  const generateCalendarDays = () => {
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 border border-gray-200 bg-gray-50" data-id="element-2227"></div>);
    }
    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isToday = new Date().toDateString() === date.toDateString();
      const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
      const dayEvents = getEventsForDate(day);
      days.push(<div key={`day-${day}`} className={`h-24 border border-gray-200 p-1 overflow-hidden cursor-pointer transition-colors ${isToday ? 'bg-blue-50' : ''} ${isSelected ? 'ring-2 ring-orange-500' : ''} hover:bg-gray-50`} onClick={() => handleDateClick(day)} data-id="element-2228">
          <div className="flex justify-between items-center mb-1" data-id="element-2229">
            <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : ''}`} data-id="element-2230">
              {day}
            </span>
            {dayEvents.length > 0 && <span className="text-xs bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center" data-id="element-2231">
                {dayEvents.length}
              </span>}
          </div>
          <div className="space-y-1 overflow-hidden" data-id="element-2232">
            {dayEvents.slice(0, 2).map(event => <div key={event.id} className={`text-xs truncate rounded px-1 py-0.5 ${event.type === 'Online' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} onClick={e => handleEventClick(event.id, e)} data-id="element-2233">
                {event.title}
              </div>)}
            {dayEvents.length > 2 && <div className="text-xs text-gray-500" data-id="element-2234">
                +{dayEvents.length - 2} more
              </div>}
          </div>
        </div>);
    }
    return days;
  };
  return <div className="bg-white rounded-lg shadow-sm p-4" data-id="element-2235">
      <div className="flex justify-between items-center mb-4" data-id="element-2236">
        <h2 className="text-lg font-semibold" data-id="element-2237">
          {monthNames[currentMonth]} {currentYear}
        </h2>
        <div className="flex space-x-2" data-id="element-2238">
          <button className="p-1 rounded-full hover:bg-gray-100" onClick={prevMonth} data-id="element-2239">
            <ChevronLeft size={20} data-id="element-2240" />
          </button>
          <button className="p-1 rounded-full hover:bg-gray-100" onClick={nextMonth} data-id="element-2241">
            <ChevronRight size={20} data-id="element-2242" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1" data-id="element-2243">
        {/* Day headers */}
        {dayNames.map(day => <div key={day} className="text-center text-sm font-medium text-gray-500 py-2" data-id="element-2244">
            {day}
          </div>)}
        {/* Calendar days */}
        {generateCalendarDays()}
      </div>
    </div>;
}